/**
 * Admin Studio v1 — AdminPlaceReportsPage
 * Uses PageHeader, StudioSurface, EmptyBlock for consistent admin UI.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import { CheckCircle, ExternalLink, MessageSquareWarning } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { PageHeader } from '@/components/admin/PageHeader';
import { StudioSurface } from '@/components/admin/StudioSurface';
import { EmptyBlock } from '@/components/admin/EmptyBlock';

interface PlaceReport {
  id: string;
  place_id: string | null;
  place_name: string | null;
  report_type: string;
  message: string;
  page_url: string | null;
  reporter_email: string | null;
  status: string;
  resolved_at: string | null;
  resolved_by: string | null;
  created_at: string;
}

export default function AdminPlaceReportsPage() {
  const [selectedReport, setSelectedReport] = useState<PlaceReport | null>(null);
  const queryClient = useQueryClient();

  const { data: reports = [], isLoading } = useQuery({
    queryKey: ['admin-place-reports'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('place_reports')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);
      if (error) throw error;
      return (data ?? []) as PlaceReport[];
    },
  });

  const resolveMutation = useMutation({
    mutationFn: async (reportId: string) => {
      const { error } = await supabase
        .from('place_reports')
        .update({ status: 'resolved', resolved_at: new Date().toISOString() })
        .eq('id', reportId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Markerad som hanterad');
      queryClient.invalidateQueries({ queryKey: ['admin-place-reports'] });
      queryClient.invalidateQueries({ queryKey: ['admin-place-reports-count'] });
      setSelectedReport(null);
    },
    onError: () => toast.error('Kunde inte uppdatera'),
  });

  const newCount = reports.filter((r) => r.status === 'new').length;

  return (
    <div>
      <PageHeader
        title="Rapporter"
        description={`Felrapporter från besökare${newCount > 0 ? ` · ${newCount} nya` : ''}`}
      />

      <div className="px-4 lg:px-8 pb-8 space-y-4">
        {/* Tabs */}
        <div className="flex gap-2">
          <Badge variant="secondary" className="px-3 py-1 text-xs font-medium">
            Felrapporter
          </Badge>
          <Badge variant="outline" className="px-3 py-1 text-xs font-medium text-muted-foreground">
            Övriga (kommer snart)
          </Badge>
        </div>

        {/* List */}
        <StudioSurface className="p-0 overflow-hidden">
          {isLoading ? (
            <div className="p-6">
              <p className="text-sm text-muted-foreground">Laddar…</p>
            </div>
          ) : reports.length === 0 ? (
            <EmptyBlock
              title="Inga rapporter"
              description="Inga felrapporter har skickats in ännu."
            />
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="text-left p-3 font-medium text-muted-foreground">Tid</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Plats</th>
                  <th className="text-left p-3 font-medium text-muted-foreground hidden sm:table-cell">Typ</th>
                  <th className="text-left p-3 font-medium text-muted-foreground hidden md:table-cell">Meddelande</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Status</th>
                </tr>
              </thead>
              <tbody>
                {reports.map((r) => (
                  <tr
                    key={r.id}
                    className="border-b last:border-0 hover:bg-muted/20 cursor-pointer transition-colors"
                    onClick={() => setSelectedReport(r)}
                  >
                    <td className="p-3 whitespace-nowrap text-muted-foreground">
                      {format(new Date(r.created_at), 'd MMM HH:mm', { locale: sv })}
                    </td>
                    <td className="p-3 font-medium max-w-[160px] truncate">
                      {r.place_name || '–'}
                    </td>
                    <td className="p-3 text-muted-foreground hidden sm:table-cell">{r.report_type}</td>
                    <td className="p-3 max-w-[280px] hidden md:table-cell">
                      <p className="line-clamp-2 text-foreground/80">{r.message}</p>
                    </td>
                    <td className="p-3">
                      {r.status === 'resolved' ? (
                        <Badge variant="outline" className="text-xs text-muted-foreground">Hanterad</Badge>
                      ) : (
                        <Badge variant="secondary" className="text-xs">Ny</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </StudioSurface>
      </div>

      {/* Detail drawer */}
      <Sheet open={!!selectedReport} onOpenChange={(open) => !open && setSelectedReport(null)}>
        <SheetContent className="sm:max-w-md">
          {selectedReport && (
            <>
              <SheetHeader>
                <SheetTitle className="text-base">
                  {selectedReport.place_name || 'Rapport'}
                </SheetTitle>
              </SheetHeader>
              <div className="mt-4 space-y-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Typ</p>
                  <p className="text-sm">{selectedReport.report_type}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Tid</p>
                  <p className="text-sm">
                    {format(new Date(selectedReport.created_at), 'd MMMM yyyy HH:mm', { locale: sv })}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Meddelande</p>
                  <p className="text-sm leading-relaxed">{selectedReport.message}</p>
                </div>
                {selectedReport.page_url && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Sida</p>
                    <a
                      href={selectedReport.page_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary hover:underline inline-flex items-center gap-1"
                    >
                      Öppna <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                )}
                {selectedReport.reporter_email && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Rapportör</p>
                    <p className="text-sm">{selectedReport.reporter_email}</p>
                  </div>
                )}
                {selectedReport.status === 'new' && (
                  <Button
                    onClick={() => resolveMutation.mutate(selectedReport.id)}
                    disabled={resolveMutation.isPending}
                    className="w-full mt-4"
                    variant="outline"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Markera som hanterad
                  </Button>
                )}
                {selectedReport.status === 'resolved' && (
                  <p className="text-xs text-muted-foreground">
                    Hanterad {selectedReport.resolved_at && format(new Date(selectedReport.resolved_at), 'd MMM yyyy', { locale: sv })}
                  </p>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
